import { FC, useEffect, useRef, useState, useMemo } from "react";
import { Theme, MessagesType } from "../../../types";
import { MenuProps } from "../../types";
import {
  megaMenuSearchBox,
  megaMenuSearchBoxActive,
  megaMenuTabBar,
  megaMenuTabBase,
  megaMenuTabActive,
  megaMenuTabInactive,
  megaMenuMarketItem,
  megaMenuEmptyText,
  megaMenuSkeletonBg,
  megaMenuInputText,
  megaMenuDismissButton,
  stylesText
} from "../../styles";
import useSearchMarket from "./hooks/useSearchMarket";
import type { MarketItem, QuoteCurrencyTab, MarketContext } from "./types";
import clsx from "clsx";
import { useTranslations } from "use-intl";
import { useStoreState } from "../../store/useStore";
import { defaultCoinIcon } from "@/nav-localized/config";
import EmptySearchDark from "@/nav-localized/assets/header/nav/empty-search-dark.svg?react";
import EmptySearchLight from "@/nav-localized/assets/header/nav/empty-search-light.svg?react";
import SearchIcon from "@/nav-localized/assets/header/nav/search.svg?react";
import ClearIcon from "@/nav-localized/assets/header/nav/clear.svg?react";
import styles from "./index.module.scss";

/**
 * isPrimary item label → 筛选值映射
 * Futures 菜单：hover 不同交易项时，右侧按对应 contract type 过滤
 * 值与 WatchList 的 FuturesStrategyEnum 一致: "usdt" (USDT-M) | "usdc" (USDC-M) | "usd" (Coin-M)
 */
const LABEL_TO_CONTRACT_TYPE: Record<string, string> = {
  usdtM: "USDT",
  usdcM: "USDC",
  coinM: "USD"
};

interface SearchMarketWidgetProps {
  theme: Theme;
  /** 所属一级菜单 label，用于确定市场上下文 */
  menuLabel?: MessagesType | "";
  /** 当前 hover 的 isPrimary 项（Futures 模式下决定 quote currency） */
  hoveredItem?: MenuProps | null;
}

/**
 * SearchMarketWidget — 搜索行情面板
 *
 * 两种模式：
 * 1. **Spot（Tab 模式）**：显示 USDT/USDC Tab，用户切换过滤
 * 2. **Futures（无 Tab 模式）**：quote currency 由 hoveredItem 决定（usdtM→USDT, usdcM→USDC, coinM→USD）
 */
const SearchMarketWidget: FC<SearchMarketWidgetProps> = ({ theme, menuLabel, hoveredItem }) => {
  const t = useTranslations("header");
  const { localeLink } = useStoreState();

  // 根据 menuLabel 确定市场上下文
  const marketContext: MarketContext = menuLabel === "futures" ? "futures" : "spot";

  // Futures 模式：从 hoveredItem 推导筛选条件（无 Tab）
  const externalFilter = useMemo(() => {
    if (marketContext !== "futures" || !hoveredItem?.label) return undefined;
    return LABEL_TO_CONTRACT_TYPE[hoveredItem.label] || undefined;
  }, [marketContext, hoveredItem?.label]);

  // 是否显示 Tab 栏：Spot 显示，Futures 不显示
  const showTabs = !externalFilter;

  // 组件挂载即代表面板可见（由 Menu.tsx 的 open 状态控制渲染）
  const visible = true;

  const { activeTab, setActiveTab, search, setSearch, results, loading, isSearching, topList } =
    useSearchMarket(visible, marketContext, externalFilter);

  // hover 切换 isPrimary 条目后，清空已输入的搜索内容（避免残留影响新的 hover 上下文）
  useEffect(() => {
    setSearch("");
  }, [setSearch, marketContext, hoveredItem?.label]);

  const displayList = isSearching ? results : topList;

  const handleNavigate = (item: MarketItem) => {
    const path =
      marketContext === "futures"
        ? `/futures${localeLink}/${item.symbol}`
        : `/spot${localeLink}/${item.symbol}`;
    window.location.href = path;
  };

  return (
    <div className="flex flex-col gap-[8px] w-full h-full">
      {/* Tab + 搜索框区域 */}
      <div
        className={clsx(
          "flex flex-col px-[8px] shrink-0 w-full",
          showTabs ? "gap-[12px]" : "pt-[12px]"
        )}
      >
        {/* Tab 栏：仅 Spot 模式显示 USDT / USDC（下划线样式） */}
        {showTabs && <TabBar theme={theme} activeTab={activeTab} onChange={setActiveTab} />}

        {/* 搜索框 */}
        <SearchBox theme={theme} value={search} onChange={setSearch} t={t} />
      </div>

      {/* 列表区域（flex-1 填满剩余空间，超出后滚动） */}
      <div
        className={clsx(
          "flex flex-col flex-1 min-h-0 w-full",
          theme === "dark" ? styles["scroll-container-dark"] : styles["scroll-container"]
        )}
      >
        {loading && !displayList.length ? (
          <LoadingSkeleton theme={theme} />
        ) : displayList.length === 0 ? (
          <EmptyState theme={theme} t={t} />
        ) : (
          displayList.map((item) => (
            <MarketRow
              key={item.symbol}
              item={item}
              theme={theme}
              mode={marketContext}
              onClick={() => handleNavigate(item)}
            />
          ))
        )}
      </div>
    </div>
  );
};

// ─── Tab Bar（USDT / USDC）──────────────────────────────────

const QUOTE_TABS: { key: QuoteCurrencyTab; label: string }[] = [
  { key: "USDT", label: "USDT" },
  { key: "USDC", label: "USDC" }
];

const TabBar: FC<{
  theme: Theme;
  activeTab: QuoteCurrencyTab;
  onChange: (tab: QuoteCurrencyTab) => void;
}> = ({ theme, activeTab, onChange }) => {
  return (
    <div className={megaMenuTabBar({ mode: theme })}>
      {QUOTE_TABS.map((tab) => {
        const isActive = activeTab === tab.key;
        return (
          <button
            key={tab.key}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onChange(tab.key);
            }}
            className={clsx(
              megaMenuTabBase(),
              isActive ? megaMenuTabActive({ mode: theme }) : megaMenuTabInactive({ mode: theme })
            )}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
};

// ─── Search Box ───────────────────────────────────────────────

const SearchBox: FC<{
  theme: Theme;
  value: string;
  onChange: (val: string) => void;
  t: ReturnType<typeof useTranslations<"header">>;
}> = ({ theme, value, onChange, t }) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);

  // 输入框有内容或聚焦时高亮边框
  const isActive = focused || value.length > 0;

  return (
    <div
      className={clsx(
        megaMenuSearchBox({ mode: theme }),
        "cursor-text w-full transition-colors",
        isActive && megaMenuSearchBoxActive({ mode: theme })
      )}
      onClick={() => inputRef.current?.focus()}
    >
      {/* 搜索图标 */}
      <SearchIcon className={clsx("shrink-0 w-[16px] h-[16px]", stylesText({ mode: theme }))} />

      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={(() => {
          try {
            const val = String(t("searchMarketPlaceholder") || "");
            // next-intl 找不到 key 时返回完整 key path（含 "."），降级为硬编码
            return val && !val.includes(".") ? val : "Search what you need";
          } catch {
            return "Search what you need";
          }
        })()}
        className={clsx(
          "flex-1 bg-transparent outline-none border-none text-[12px] leading-[18px]",
          megaMenuInputText({ mode: theme })
        )}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onClick={(e) => e.stopPropagation()}
      />

      {/* 清除按钮 */}
      {value && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onChange("");
          }}
          className={clsx(
            "shrink-0 w-[16px] h-[16px] cursor-pointer rounded-full transition-colors",
            megaMenuDismissButton({ mode: theme })
          )}
        >
          <ClearIcon className="shrink-0" />
        </button>
      )}
    </div>
  );
};

// ─── Market Row（匹配 Figma 设计稿）────────────────────────

const MarketRow: FC<{
  item: MarketItem;
  theme: Theme;
  mode: MarketContext;
  onClick: () => void;
}> = ({ item, theme, mode, onClick }) => {
  const isPositive = item.change24 >= 0;
  const [imgError, setImgError] = useState(false);

  // Spot 带 "/"，Futures 直接拼接（对齐 WatchList SymbolList 逻辑）
  const displayName =
    mode === "spot"
      ? `${item.baseCurrency}/${item.quoteCurrency}`
      : `${item.baseCurrency}${item.quoteCurrency}`;

  return (
    <div className={megaMenuMarketItem({ mode: theme })} onClick={onClick}>
      {/* 左侧：币种图标 + 交易对名称 */}
      <div className="flex items-center gap-[4px] shrink-0">
        <div className="relative shrink-0 w-[20px] h-[20px]">
          <img
            src={!item.logo || imgError ? defaultCoinIcon : item.logo}
            alt={item.baseCurrency}
            className="block max-w-none w-full h-full rounded-full"
            onError={() => setImgError(true)}
          />
        </div>
        <span
          className={clsx(
            "text-[12px] font-medium leading-[18px] w-[142px]",
            stylesText({ mode: theme })
          )}
        >
          {displayName}
        </span>
      </div>

      {/* 右侧：价格（上）+ 涨跌幅（下），纵向排列右对齐 */}
      <div className="flex flex-1 flex-row items-center self-stretch">
        <div className="flex flex-1 flex-col items-end justify-center text-[12px] font-medium leading-[18px] text-right">
          <span
            dir="ltr"
            className={clsx("tabular-nums min-w-full w-min", stylesText({ mode: theme }))}
          >
            {toNonExponential(item.price)}
          </span>
          <span
            dir="ltr"
            className={clsx(
              "tabular-nums w-[100px]",
              isPositive ? "text-light-green" : "text-light-red"
            )}
          >
            {formatChange24(item.change24)}
          </span>
        </div>
      </div>
    </div>
  );
};

// ─── Empty State ──────────────────────────────────────────────

const EmptyState: FC<{
  theme: Theme;
  t: ReturnType<typeof useTranslations<"header">>;
}> = ({ theme, t }) => {
  const EmptyIcon = theme === "dark" ? EmptySearchDark : EmptySearchLight;

  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-[8px] py-[56px]">
      <EmptyIcon className="w-[72px] h-[72px] shrink-0" />
      <span className={megaMenuEmptyText({ mode: theme })}>
        {t("noSearchResults") || "No data"}
      </span>
    </div>
  );
};

// ─── Loading Skeleton ─────────────────────────────────────────

const LoadingSkeleton: FC<{ theme: Theme }> = ({ theme }) => {
  const skeletonBg = megaMenuSkeletonBg({ mode: theme });

  return (
    <div className="flex flex-col">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
        <div key={i} className="flex items-center px-[8px] py-[12px]">
          {/* 左侧：图标占位 + 名称占位（同高，无间距连在一起） */}
          <div
            className={clsx(
              "flex items-center shrink-0 w-[100px] h-[20px] rounded-[4px] animate-pulse",
              skeletonBg
            )}
          />

          {/* 右侧：价格 + 涨跌幅 两行占位 */}
          <div className="flex flex-1 flex-col items-end gap-[6px]">
            <div className={clsx("w-[60px] h-[12px] rounded-[4px] animate-pulse", skeletonBg)} />
            <div className={clsx("w-[48px] h-[12px] rounded-[4px] animate-pulse", skeletonBg)} />
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Utils（与 @blofin/blofin-utils format 逻辑一致） ────────

/** 防止科学计数法，保留 API 原始精度（对齐 WatchList toNonExponential） */
function toNonExponential(num: number): string {
  if (!Number.isFinite(num)) return "0";
  const m = num.toExponential().match(/\d(?:\.(\d*))?e([+-]\d+)/);
  return num.toFixed(Math.max(0, (m?.[1] || "").length - Number(m?.[2] || 0)));
}

/** 向下截断到指定小数位（对齐 WatchList formatFloor / getBit） */
function formatFloor(value: number, precision: number): string {
  if (!Number.isFinite(value)) return (0).toFixed(precision);
  if (precision <= 0) return Math.floor(value).toString();
  const str = value.toString();
  const dotIndex = str.indexOf(".");
  if (dotIndex === -1) return str + "." + "0".repeat(precision);
  const decimal = str.slice(dotIndex + 1, dotIndex + precision + 1);
  return str.slice(0, dotIndex) + "." + decimal.padEnd(precision, "0");
}

/** 格式化涨跌幅（对齐 WatchList change24Text） */
function formatChange24(value: number): string {
  if (!Number.isFinite(value)) return `+${formatFloor(0, 2)}%`;
  if (value > 0) return `+${formatFloor(value, 2)}%`;
  if (value < 0) return `${formatFloor(value, 2)}%`;
  return `+${formatFloor(0, 2)}%`;
}

export default SearchMarketWidget;
